import { createSlice } from "@reduxjs/toolkit";
import { PerformanceReviewState } from './types';
import {
    getAllPerformanceReviews,
    getPerformanceReviewById,
    getMyPerformanceReviews,
    getManagerPerformanceReviews,
    getAllReviewCycles,
    createReviewCycle,
    updateReviewCycle,
    getAllReviewTemplates,
    createReviewTemplate,
    updateReviewTemplate,
    submitSelfReview,
    submitManagerReview,
    submitHRReview,
    acknowledgeReview,
    getAdminPerformanceReviews,
    requestReviewRevision,
    getReviewHistory,
    getReviewStatistics
} from './thunk';

const initialState: PerformanceReviewState = {
    reviews: [],
    cycles: [],
    templates: [],
    currentReview: null,
    currentCycle: null,
    currentTemplate: null,
    managerReviews: [],
    adminReviews: [],
    reviewHistory: [],
    statistics: null,
    loading: false,
    error: null
};

const performanceReviewSlice = createSlice({
    name: "performanceReview",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentReview: (state, action) => {
            state.currentReview = action.payload;
        },
        setCurrentCycle: (state, action) => {
            state.currentCycle = action.payload;
        },
        setCurrentTemplate: (state, action) => {
            state.currentTemplate = action.payload;
        },
        clearCurrentReview: (state) => {
            state.currentReview = null;
        },
        clearCurrentCycle: (state) => {
            state.currentCycle = null;
        },
        clearCurrentTemplate: (state) => {
            state.currentTemplate = null;
        }
    },
    extraReducers: (builder) => {
        // Performance Reviews
        builder
            .addCase(getAllPerformanceReviews.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllPerformanceReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews = action.payload;
            })
            .addCase(getAllPerformanceReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(getPerformanceReviewById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getPerformanceReviewById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentReview = action.payload;
            })
            .addCase(getPerformanceReviewById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(getMyPerformanceReviews.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getMyPerformanceReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews = action.payload;
            })
            .addCase(getMyPerformanceReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(getManagerPerformanceReviews.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getManagerPerformanceReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.managerReviews = action.payload;
            })
            .addCase(getManagerPerformanceReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Review Cycles
        builder
            .addCase(getAllReviewCycles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllReviewCycles.fulfilled, (state, action) => {
                state.loading = false;
                state.cycles = action.payload;
            })
            .addCase(getAllReviewCycles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createReviewCycle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createReviewCycle.fulfilled, (state, action) => {
                state.loading = false;
                state.cycles.push(action.payload);
            })
            .addCase(createReviewCycle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateReviewCycle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateReviewCycle.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.cycles.findIndex(cycle => cycle.id === action.payload.id);
                if (index !== -1) {
                    state.cycles[index] = action.payload;
                }
            })
            .addCase(updateReviewCycle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Review Templates
        builder
            .addCase(getAllReviewTemplates.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllReviewTemplates.fulfilled, (state, action) => {
                state.loading = false;
                state.templates = action.payload;
            })
            .addCase(getAllReviewTemplates.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createReviewTemplate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createReviewTemplate.fulfilled, (state, action) => {
                state.loading = false;
                state.templates.push(action.payload);
            })
            .addCase(createReviewTemplate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateReviewTemplate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateReviewTemplate.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.templates.findIndex(template => template.id === action.payload.id);
                if (index !== -1) {
                    state.templates[index] = action.payload;
                }
            })
            .addCase(updateReviewTemplate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Review Submissions
        builder
            .addCase(submitSelfReview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(submitSelfReview.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.reviews.findIndex(review => review.id === action.payload.id);
                if (index !== -1) {
                    state.reviews[index] = action.payload;
                }
                if (state.currentReview && state.currentReview.id === action.payload.id) {
                    state.currentReview = action.payload;
                }
            })
            .addCase(submitSelfReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(submitManagerReview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(submitManagerReview.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.reviews.findIndex(review => review.id === action.payload.id);
                if (index !== -1) {
                    state.reviews[index] = action.payload;
                }
                if (state.currentReview && state.currentReview.id === action.payload.id) {
                    state.currentReview = action.payload;
                }
            })
            .addCase(submitManagerReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(submitHRReview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(submitHRReview.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.reviews.findIndex(review => review.id === action.payload.id);
                if (index !== -1) {
                    state.reviews[index] = action.payload;
                }
                if (state.currentReview && state.currentReview.id === action.payload.id) {
                    state.currentReview = action.payload;
                }
            })
            .addCase(submitHRReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(acknowledgeReview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(acknowledgeReview.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.reviews.findIndex(review => review.id === action.payload.id);
                if (index !== -1) {
                    state.reviews[index] = action.payload;
                }
                if (state.currentReview && state.currentReview.id === action.payload.id) {
                    state.currentReview = action.payload;
                }
            })
            .addCase(acknowledgeReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(getAdminPerformanceReviews.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAdminPerformanceReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.adminReviews = action.payload;
            })
            .addCase(getAdminPerformanceReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(requestReviewRevision.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(requestReviewRevision.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.reviews.findIndex(review => review.id === action.payload.id);
                if (index !== -1) {
                    state.reviews[index] = action.payload;
                }
                if (state.currentReview && state.currentReview.id === action.payload.id) {
                    state.currentReview = action.payload;
                }
            })
            .addCase(requestReviewRevision.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(getReviewHistory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getReviewHistory.fulfilled, (state, action) => {
                state.loading = false;
                state.reviewHistory = action.payload;
            })
            .addCase(getReviewHistory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(getReviewStatistics.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getReviewStatistics.fulfilled, (state, action) => {
                state.loading = false;
                state.statistics = action.payload;
            })
            .addCase(getReviewStatistics.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

export const {
    clearError,
    setCurrentReview,
    setCurrentCycle,
    setCurrentTemplate,
    clearCurrentReview,
    clearCurrentCycle,
    clearCurrentTemplate
} = performanceReviewSlice.actions;

export default performanceReviewSlice.reducer;
